/* eslint-disable jsx-a11y/media-has-caption */
import { useHistoryVideo } from '@/app/main/subadmin/livestreaming/history/HistoryProvider';
import JMuxer from 'jmuxer';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import H264Controls from '@/app/main/subadmin/livestreaming/history/PBJ/H264VideoPlayer/H264Controls';
import { processMultipartStream } from './processMultipartStream';
import { processStream } from '@/app/main/subadmin/livestreaming/history/PBJ/H264VideoPlayer/processStream';
import { GhettoStream, MuLawDecoder, PcmAudioPlayer } from 'ui3';

const H264VideoPlayer = ({ url, audioUrl, roomData, setIsLoading }) => {
	const playerRef = useRef(null);
	const boxRef = useRef(null);
	const { volume, setVolume } = useHistoryVideo();
	const [error, setError] = useState('');
	const [isSeeking, setIsSeeking] = useState(false);
	const [pendingSeekTime, setPendingSeekTime] = useState(0);
	const [isBuffering, setIsBuffering] = useState(false);
	const pcmPlayerRef = useRef(null);
	const volumeRef = useRef(volume);
	const jmuxerRef = useRef(null);
	const audioAbortControllerRef = useRef(null);
	const videoAbortControllerRef = useRef(null);
	const [seekOffset, setSeekOffset] = useState(0);
	const [currentSliderValue, setCurrentSliderValue] = useState(0);
	const [speed, setSpeed] = useState(100);
	const [isPlaying, setIsPlaying] = useState(false);
	const isPausedRef = useRef(false);

	const maxDuration = 1200; // 20 minutes in seconds

	// Extract boundary from Content-Type header during fetch
	const [boundary, setBoundary] = useState('myboundary');

	// For Salient, we don't use pos parameter but track clip start time and seek offset
	const clipStartTime = useMemo(() => {
		if (!url) return null;
		try {
			const urlObj = new URL(url);
			const startParam = urlObj.searchParams.get('start');
			return startParam ? new Date(startParam).getTime() : null;
		} catch (e) {
			return null;
		}
	}, [url]);

	// Track current playback position within the clip (in seconds from clip start)
	const [currentSeekOffset, setCurrentSeekOffset] = useState(0);

	// Update when URL changes
	useEffect(() => {
		setCurrentSeekOffset(0);
		setPendingSeekTime(0);
		setIsSeeking(false);
	}, [url]);

	// Remove this old effect as we're using currentSeekOffset directly

	useEffect(() => {
		const videoElement = playerRef.current;
		if (!videoElement) return;

		const updateSliderValue = () => {
			const videoCurrentTime = videoElement.currentTime;
			const sliderValue = videoCurrentTime + currentSeekOffset;
			setCurrentSliderValue(sliderValue);
		};

		videoElement.addEventListener('timeupdate', updateSliderValue);

		return () => videoElement.removeEventListener('timeupdate', updateSliderValue);
	}, [playerRef, currentSeekOffset]);

	const convertSliderValueToTimestamp = useCallback(
		(sliderValue) => {
			if (clipStartTime === null) {
				console.error('Clip start time is not set.');
				return null;
			}
			// Convert slider value (seconds) to new clip start time
			const newStartTime = clipStartTime + (sliderValue * 1000);
			return newStartTime;
		},
		[clipStartTime],
	);

	const getStreamUrl = useCallback(() => {
		if (!url) return null;
		
		try {
			const urlObj = new URL(url);
			
			// For Salient, we modify start/stop times instead of using pos
			if (clipStartTime !== null && currentSeekOffset > 0) {
				const newStartTime = new Date(clipStartTime + (currentSeekOffset * 1000));
				const originalStop = urlObj.searchParams.get('stop');
				
				urlObj.searchParams.set('start', newStartTime.toISOString());
				
				// If there was an original stop time, maintain the same duration
				if (originalStop) {
					const originalStopTime = new Date(originalStop).getTime();
					const originalStartTime = new Date(urlObj.searchParams.get('start')).getTime();
					const duration = originalStopTime - clipStartTime;
					const newStopTime = new Date(newStartTime.getTime() + duration);
					urlObj.searchParams.set('stop', newStopTime.toISOString());
				}
			}

			return urlObj.toString();
		} catch (e) {
			console.error('Error creating stream URL:', e);
			return null;
		}
	}, [url, clipStartTime, currentSeekOffset]);

	const getAudioStreamUrl = useCallback(() => {
		if (!audioUrl) return null;
		
		try {
			const urlObj = new URL(audioUrl);
			
			// For Salient audio, we also modify start/stop times like video
			if (clipStartTime !== null && currentSeekOffset > 0) {
				const newStartTime = new Date(clipStartTime + (currentSeekOffset * 1000));
				const originalStop = urlObj.searchParams.get('stop');
				
				urlObj.searchParams.set('start', newStartTime.toISOString());
				
				// If there was an original stop time, maintain the same duration
				if (originalStop) {
					const originalStopTime = new Date(originalStop).getTime();
					const duration = originalStopTime - clipStartTime;
					const newStopTime = new Date(newStartTime.getTime() + duration);
					urlObj.searchParams.set('stop', newStopTime.toISOString());
				}
			}

			return urlObj.toString();
		} catch (e) {
			console.error('Error creating audio stream URL:', e);
			return null;
		}
	}, [audioUrl, currentSeekOffset, speed, clipStartTime]);

	const isVideoPlaying = (video) => {
		if (video) {
			return !!(video.currentTime > 0 && !video.paused && !video.ended && video.readyState > video.HAVE_FUTURE_DATA);
		}
		return false;
	};

	const callStreamEnded = useCallback((abortController) => {
		return (reader) => {
			if (abortController) {
				abortController.abort();
			}

			if (reader) {
				const cancelPromise = reader.cancel('Streaming canceled');
				if (cancelPromise && cancelPromise.catch) {
					cancelPromise.catch((e) => {
						if (e && (e.code === DOMException.ABORT_ERR || e.code === DOMException.INVALID_STATE_ERR)) {
							// Expected result. Suppress error.
						} else {
							console.error(e);
						}
					});
				}
				reader = null;
			}
		};
	}, []);

	// Video frame callback for multipart stream
	const callVideoFrameCallback = useCallback((jmuxer, player) => {
		let init = false;

		return (frame) => {
			// Handle H264 video frames
			if (frame.contentType === 'image/jpeg' || frame.contentType === 'video/h264' || frame.contentType === 'application/octet-stream') {
				if (jmuxer && frame.data) {
					jmuxer.feed({
						video: frame.data,
					});

					// Start playing once we have some data streaming in
					if (!init && !isVideoPlaying(player)) {
						const playPromise = player.play();
						if (playPromise !== undefined) {
							playPromise.then(() => {
								setIsPlaying(true);
							}).catch((e) => {
								console.error('Error playing video:', e);
							});
						}
						init = true;
					}
				}
			}
		};
	}, []);

	// Audio frame callback for PBJ-style audio stream
	const callAudioFrameCallback = useCallback((jmuxer, pcmPlayer, muLawDecoder, player) => {
		return (frame) => {
			if (frame.isAudio) {
				// The only supported format is mu-law encoding with one audio channel.
				if (frame.format.wFormatTag === 7 && frame.format.nChannels === 1) {
					// 7 is mu-law, mu-law is 8 bits per sample but decodes to 16 bits per sample.
					const pcm16Bit = muLawDecoder.DecodeUint8ArrayToFloat32Array(frame.frameData);
					pcmPlayer.AcceptBuffer([pcm16Bit], frame.format.nSamplesPerSec);
				} else if (frame.format.wFormatTag === 61868) {
					pcmPlayer.DecodeAndPlayAudioData(frame.frameData, frame.format.nSamplesPerSec);
				} else {
					console.log('Unsupported audio frame format', frame.format, frame);
				}
			}
		};
	}, []);

	// Update volumeRef when volume changes
	useEffect(() => {
		volumeRef.current = volume;
		if (pcmPlayerRef.current) {
			pcmPlayerRef.current.SetAudioVolumeFromSettings(volume);
		}
		if (playerRef.current) {
			playerRef.current.volume = volume;
		}
	}, [volume]);

	const fetchVideoStream = useCallback(
		(streamUrl, fetchArgs, player, abortController) => {
			if (jmuxerRef.current) {
				jmuxerRef.current.destroy();
				jmuxerRef.current = null;
			}

			const jmuxer = new JMuxer({
				node: player,
				mode: 'video',
				maxDelay: 1000,
				flushingTime: 1000,
				clearBuffer: true,
				debug: false,
				fps: 30,
			});

			jmuxer.cancelDelay = function () {
				if (
					this.node.buffered &&
					this.node.buffered.length > 0 &&
					!this.node.seeking &&
					!this.node.paused
				) {
					const end = this.node.buffered.end(0);
					if (end - this.node.currentTime > this.options.maxDelay / 1000) {
						this.node.currentTime = end - 0.001;
					}
				}
			};

			jmuxerRef.current = jmuxer;

			const onEndStream = callStreamEnded(abortController);
			const frameCallback = callVideoFrameCallback(jmuxerRef.current, player);

			fetch(streamUrl, fetchArgs)
				.then((res) => {
					if (res.status === 200) {
						const contentType = res.headers.get('Content-Type');
						
						if (contentType && contentType.includes('multipart/x-mixed-replace')) {
							setError('');
							
							// Extract boundary from Content-Type header
							let streamBoundary = boundary; // default
							const boundaryMatch = contentType.match(/boundary=([^;,\s]+)/);
							if (boundaryMatch) {
								streamBoundary = boundaryMatch[1];
								setBoundary(streamBoundary);
								}
							
							const reader = res.body.getReader();
							
							// Start processing the multipart stream
							processMultipartStream(reader, streamBoundary, frameCallback, onEndStream, isPausedRef);
						} else {
							setError('Invalid video stream format - expected multipart/x-mixed-replace');
							onEndStream();
						}
					} else {
						setError(`Failed to load video stream (${res.status})`);
						onEndStream();
					}
				})
				.catch((e) => {
					if (e.name === 'AbortError') {
						return;
					}
					setError('Video server is unreachable');
					console.error('Video fetch error:', e);
					onEndStream();
				})
				.finally(() => setIsLoading(false));
		},
		[callStreamEnded, callVideoFrameCallback, setIsLoading, boundary],
	);

	const fetchAudioStream = useCallback(
		(streamUrl, fetchArgs, player, abortController) => {
			if (pcmPlayerRef.current) {
				pcmPlayerRef.current.Reset();
			}

			const stream = new GhettoStream();
			const pcmPlayer = new PcmAudioPlayer();
			pcmPlayerRef.current = pcmPlayer;
			const muLawDecoder = new MuLawDecoder();
			pcmPlayer.SetAudioVolumeFromSettings(volumeRef.current);

			const onEndStream = callStreamEnded(abortController);
			const frameCallback = callAudioFrameCallback(null, pcmPlayer, muLawDecoder, player);

			fetch(streamUrl, fetchArgs)
				.then((res) => {
					const contentType = res.headers.get('Content-Type');
					if (res.status === 200 && contentType === 'video/mpeg') {
						const reader = res.body.getReader();
						return processStream(stream, reader, frameCallback, onEndStream, isPausedRef);
					} else {
						console.error('Failed to load audio stream');
						onEndStream();
					}
				})
				.catch((e) => {
					if (e.name === 'AbortError') {
						return;
					}
					console.error('Audio fetch error:', e);
					onEndStream();
				});
		},
		[callStreamEnded, callAudioFrameCallback],
	);

	useEffect(() => {
		const videoAbortController = new AbortController();
		const audioAbortController = new AbortController();
		videoAbortControllerRef.current = videoAbortController;
		audioAbortControllerRef.current = audioAbortController;
		const videoElement = playerRef.current;

		if (videoElement && url) {
			const fetchArgs = { credentials: 'same-origin' };
			const streamUrl = getStreamUrl();
			const audioStreamUrl = getAudioStreamUrl();

			if (!streamUrl) {
				console.error('Video stream URL is invalid. Aborting stream fetch.');
				return;
			}

			// Fetch video stream
			fetchVideoStream(streamUrl, { ...fetchArgs, signal: videoAbortController.signal }, videoElement, videoAbortController);

      // TODO: Uncomment this once Salient enables audio.
			// Fetch audio stream if available
			// if (audioStreamUrl) {
			// 	fetchAudioStream(audioStreamUrl, { ...fetchArgs, signal: audioAbortController.signal }, videoElement, audioAbortController);
			// }

			videoElement.onloadeddata = () => {
				videoElement
					.play()
					.then(() => {
						setIsBuffering(false);
						setIsPlaying(true);
					})
					.catch((e) => console.error('Error attempting to play video:', e));
			};

			videoElement.onwaiting = () => {
				setIsBuffering(true);
			};

			videoElement.oncanplay = () => {
				if (isBuffering) {
					setIsBuffering(false);
					videoElement
						.play()
						.then(() => {
							setIsBuffering(false);
							setIsPlaying(true);
						})
						.catch((e) => console.error('Error attempting to resume video:', e));
				}
			};

			return () => {
				if (pcmPlayerRef.current) {
					pcmPlayerRef.current.Reset();
				}
				if (videoAbortControllerRef.current) {
					videoAbortControllerRef.current.abort();
				}
				if (audioAbortControllerRef.current) {
					audioAbortControllerRef.current.abort();
				}
				videoElement.onloadeddata = null;
				videoElement.onwaiting = null;
				videoElement.oncanplay = null;
			};
		}
	}, [currentSeekOffset, speed, url, audioUrl, getStreamUrl, getAudioStreamUrl]);

	const handleSliderChangeCommitted = useCallback(
		(event, newValue) => {
			const videoElement = playerRef.current;
			if (videoElement) {
				videoElement.pause();
				setIsPlaying(false);
			}
			setIsSeeking(false);
			setPendingSeekTime(0);

			// Update the seek offset, which will trigger a new stream request
			setCurrentSeekOffset(newValue);
		},
		[playerRef],
	);

	const handleSliderChange = useCallback((event, newValue) => {
		setPendingSeekTime(newValue / (speed / 100));
		setIsSeeking(true);
	}, [speed]);

	const handleSliderChangeStart = () => setIsSeeking(true);

	const handleSkip = useCallback(
		(seconds) => {
			const videoElement = playerRef.current;
			if (videoElement) {
				videoElement.pause();
				setIsPlaying(false);

				const newSeekOffset = currentSeekOffset + seconds;
				const clampedSeekOffset = Math.max(0, Math.min(newSeekOffset, maxDuration));

				setPendingSeekTime(clampedSeekOffset);
				setIsSeeking(false);

				// Update seek offset, which will trigger a new stream request
				setCurrentSeekOffset(clampedSeekOffset);
			}
		},
		[playerRef, currentSeekOffset, maxDuration],
	);

	const handleVolumeChange = (newVolume) => {
		setVolume(newVolume);

		if (pcmPlayerRef.current) {
			pcmPlayerRef.current.SetAudioVolumeFromSettings(newVolume);
		}
		if (playerRef.current) {
			playerRef.current.volume = newVolume;
		}
	};

	const handlePlaybackSpeedChange = useCallback(
		(newSpeed) => {
			const videoElement = playerRef.current;
			if (videoElement) {
				const currentVideoTime = videoElement.currentTime;
				videoElement.pause();
				setIsPlaying(false);

				// Update seek offset to current playback position
				const newSeekOffset = currentSeekOffset + currentVideoTime;
				setCurrentSeekOffset(newSeekOffset);
				setSpeed(newSpeed);
			}
		},
		[currentSeekOffset, playerRef],
	);

	useEffect(() => {
		const videoElement = playerRef.current;

		const handlePlay = () => {
			isPausedRef.current = false;
			setIsPlaying(true);
			pcmPlayerRef.current?.Resume();
		};

		const handlePause = () => {
			isPausedRef.current = true;
			setIsPlaying(false);
			pcmPlayerRef.current?.Pause();
		};

		if (videoElement) {
			videoElement.addEventListener('play', handlePlay);
			videoElement.addEventListener('pause', handlePause);
		}

		return () => {
			if (videoElement) {
				videoElement.removeEventListener('play', handlePlay);
				videoElement.removeEventListener('pause', handlePause);
			}
		};
	}, []);

	return (
		<>
			{error ? <h3 className="text-red-500">{error}</h3> : null}

			<div className="relative overflow-hidden" ref={boxRef}>
				<video 
					className="rounded-lg history-video-player" 
					id="salient-h264-video" 
					ref={playerRef}
					crossOrigin="anonymous"
				/>

				<div className="mx-auto relative">
					<H264Controls
						boxRef={boxRef}
						currentTime={isSeeking ? pendingSeekTime : currentSliderValue}
						handleSkip={handleSkip}
						handleSliderChange={handleSliderChange}
						handleSliderChangeCommitted={handleSliderChangeCommitted}
						handleSliderChangeStart={handleSliderChangeStart}
						initialPosition={clipStartTime}
						maxDuration={maxDuration}
						onPlaybackSpeedChange={handlePlaybackSpeedChange}
						onVolumeChange={handleVolumeChange}
						playbackSpeed={speed}
						playerRef={playerRef}
						room={roomData}
					/>
				</div>
			</div>
		</>
	);
};

export default H264VideoPlayer;
